import {
    Assignment,
    Assessment,
    Folder,
    Timeline,
    Groups,
    Timer,
    AccountBalance,
    AccessTime,
    BeachAccess,
    EventBusy,
    ListAlt,
    CheckCircle,
    Comment,
    Receipt,
    CalendarMonth,
    BarChart,
    AutoAwesome,
    AssignmentLate,
    PlaylistAddCheck,
} from '@mui/icons-material';

// Widget Components
import TaskSummaryWidget from './TaskSummaryWidget';
import ResourceUtilizationWidget from './ResourceUtilizationWidget';
import ActiveProjectsWidget from './ActiveProjectsWidget';
import TeamActivityWidget from './TeamActivityWidget';
import AllRunningTimersWidget from './AllRunningTimersWidget';
import AccountProjectsWidget from './AccountProjectsWidget';
import ProjectProgressWidget from './ProjectProgressWidget';
import BudgetTrackerWidget from './BudgetTrackerWidget';
import MyTimeWidget from './MyTimeWidget';
import RunningTimerWidget from './RunningTimerWidget';
import TimeOffSummaryWidget from './TimeOffSummaryWidget';
import TimeOffApprovalWidget from './TimeOffApprovalWidget';
import MyAssignmentsWidget from './MyAssignmentsWidget';
import ClientTimeSummaryWidget from './ClientTimeSummaryWidget';
import ProjectsReadyToCloseWidget from './ProjectsReadyToCloseWidget';
import UnansweredClientCommentsWidget from './UnansweredClientCommentsWidget';
import MonthlyStatusBreakdownWidget from './MonthlyStatusBreakdownWidget';
import InvoicesWidget from './InvoicesWidget';
import ClientQuotesWidget from './ClientQuotesWidget';
import ManagerQuotesWidget from './ManagerQuotesWidget';
import MyCalendarWidget from './MyCalendarWidget';
import TeamCalendarWidget from './TeamCalendarWidget';
import PendingSuggestionsWidget from './PendingSuggestionsWidget';
import UnassignedWorkWidget from './UnassignedWorkWidget';
import ProjectsWithoutTasksWidget from './ProjectsWithoutTasksWidget';

/**
 * Widget Registry - Central registry of all available dashboard widgets
 *
 * Each widget definition includes:
 * - id: Unique identifier for the widget type
 * - name: Display name
 * - description: Widget description shown in add widget dialog
 * - icon: MUI icon component
 * - component: React component to render
 * - defaultSize: Default grid size { w, h }
 * - minSize: Minimum grid size { w, h }
 * - permissions: Array of required permissions (empty = available to all)
 * - negativePermissions: Array of permissions that are not allowed (empty = allowed)
 * - category: Widget category for organization
 */

const widgetDefinitions = [
    // =====================
    // Internal User Widgets
    // =====================
    {
        id: 'tasks',
        name: 'Task Summary',
        description: 'View your open tasks by priority and status',
        icon: Assignment,
        component: TaskSummaryWidget,
        defaultSize: { w: 4, h: 5 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },
    {
        id: 'my-time',
        name: 'My Time',
        description: 'Your hours for the period, plus days you never logged and days over target',
        icon: AccessTime,
        component: MyTimeWidget,
        defaultSize: { w: 4, h: 8 },
        minSize: { w: 3, h: 8 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },
    {
        id: 'running-timer',
        name: 'My Timer',
        description: 'View and control your currently running timer',
        icon: Timer,
        component: RunningTimerWidget,
        defaultSize: { w: 3, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },
    {
        id: 'time-off',
        name: 'Time Off',
        description: 'Days taken this year and last year, by type',
        icon: BeachAccess,
        component: TimeOffSummaryWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },
    {
        id: 'my-assignments',
        name: 'My Assignments',
        description: 'Tasks you are responsible for, ordered by urgency',
        icon: ListAlt,
        component: MyAssignmentsWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },
    {
        id: 'my-calendar',
        name: 'My Calendar',
        description: 'A 15-day timeline of your assigned projects and tasks',
        icon: CalendarMonth,
        component: MyCalendarWidget,
        defaultSize: { w: 8, h: 6 },
        minSize: { w: 6, h: 4 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'internal',
    },

    // =====================
    // Management Widgets
    // =====================
    {
        id: 'resource-utilization',
        name: 'Resource Utilization',
        description: 'View team workload and utilization rates',
        icon: Assessment,
        component: ResourceUtilizationWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'active-projects',
        name: 'Active Projects',
        description: 'Overview of all projects grouped by status',
        icon: Folder,
        component: ActiveProjectsWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'team-activity',
        name: 'Team Activity',
        description: 'Recent activity across the organization',
        icon: Groups,
        component: TeamActivityWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'all-running-timers',
        name: 'All Running Timers',
        description: 'See who is currently tracking time',
        icon: Timer,
        component: AllRunningTimersWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'account-projects',
        name: 'Account Projects',
        description: 'View projects for your pinned accounts',
        icon: Folder,
        component: AccountProjectsWidget,
        defaultSize: { w: 3, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'budget-tracker',
        name: 'Budget Tracker',
        description: 'Monitor retainer budgets and spending',
        icon: AccountBalance,
        component: BudgetTrackerWidget,
        defaultSize: { w: 5, h: 7 },
        minSize: { w: 4, h: 5 },
        permissions: ['canRunReports'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'monthly-status-breakdown',
        name: 'Monthly Status Breakdown',
        description: 'Stacked monthly counts of projects by status over the last 3–12 months',
        icon: BarChart,
        component: MonthlyStatusBreakdownWidget,
        defaultSize: { w: 6, h: 6 },
        minSize: { w: 4, h: 5 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'projects-ready-to-close',
        name: 'Projects Ready to Complete',
        description: 'List of active projects with 100% progress',
        icon: CheckCircle,
        component: ProjectsReadyToCloseWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'unanswered-client-comments',
        name: 'Unanswered Client Comments',
        description: 'Projects where the most recent comment is from a client',
        icon: Comment,
        component: UnansweredClientCommentsWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeInternal'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'projects-without-tasks',
        name: 'Projects Without Tasks',
        description: 'Open projects that nobody has broken into tasks yet, newest first',
        icon: PlaylistAddCheck,
        component: ProjectsWithoutTasksWidget,
        defaultSize: { w: 6, h: 7 },
        minSize: { w: 4, h: 5 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'unassigned-work',
        name: 'Unassigned / No Responsible',
        description: 'Projects and tasks with no one responsible — including client-created items — ready to reassign',
        icon: AssignmentLate,
        component: UnassignedWorkWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'manager-quotes',
        name: 'Open Quotes',
        description: 'View open quote requests (Pending, Draft, Under Review)',
        icon: Receipt,
        component: ManagerQuotesWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: ['canManageQuotes'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'pending-suggestions',
        name: 'Suggested Actions',
        description: 'Actions the assistant recommends, waiting for your approval',
        icon: AutoAwesome,
        component: PendingSuggestionsWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 4 },
        permissions: ['canSeeSuggestions'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'time-off-approval',
        name: 'Time Off Approvals',
        description: 'Approve pending time off requests and see who is out',
        icon: EventBusy,
        component: TimeOffApprovalWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: ['reviewTimeOff'],
        negativePermissions: [],
        category: 'management',
    },
    {
        id: 'team-calendar',
        name: 'Active Tasks Calendar',
        description: 'A 15-day timeline of all active tasks across the team',
        icon: CalendarMonth,
        component: TeamCalendarWidget,
        defaultSize: { w: 8, h: 6 },
        minSize: { w: 6, h: 4 },
        permissions: ['canSeeManagement'],
        negativePermissions: [],
        category: 'management',
    },

    // =====================
    // Client Widgets
    // =====================

    {
        id: 'project-progress',
        name: 'Project Progress',
        description: 'Track progress of active projects',
        icon: Timeline,
        component: ProjectProgressWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: [],
        negativePermissions: ['canSeeInternal'],
        category: 'client',
    },
    {
        id: 'client-time-summary',
        name: 'Monthly Time Summary',
        description: 'View your time usage by billing type for the current month',
        icon: AccessTime,
        component: ClientTimeSummaryWidget,
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 5 },
        permissions: [],
        negativePermissions: ['canSeeInternal'],
        category: 'client',
    },
    {
        id: 'invoices',
        name: 'Invoices',
        description: 'List of invoices for billing entities you have access to',
        icon: Receipt,
        component: InvoicesWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: [],
        negativePermissions: ['canSeeInternal'],
        category: 'client',
    },
    {
        id: 'client-quotes',
        name: 'My Quotes',
        description: 'View your quote requests and their current status',
        icon: Receipt,
        component: ClientQuotesWidget,
        defaultSize: { w: 4, h: 7 },
        minSize: { w: 3, h: 5 },
        permissions: [],
        negativePermissions: ['canSeeInternal'],
        category: 'client',
    },
    {
        id: 'Client-calendar',
        name: 'Active Tasks Calendar',
        description: 'A 15-day timeline of all active tasks across the team',
        icon: CalendarMonth,
        component: TeamCalendarWidget,
        defaultSize: { w: 8, h: 6 },
        minSize: { w: 6, h: 4 },
        permissions: [],
        negativePermissions: ['canSeeInternal'],
        category: 'client',
    },

];

class WidgetRegistry {
    constructor() {
        this.widgets = new Map();
        this._initialize();
    }

    _initialize() {
        widgetDefinitions.forEach(widget => {
            this.widgets.set(widget.id, widget);
        });
    }

    /**
     * Get all widget definitions
     */
    getAll() {
        return Array.from(this.widgets.values());
    }

    /**
     * Get widget definition by ID
     */
    getById(id) {
        return this.widgets.get(id);
    }

    /**
     * Get widgets by category
     */
    getByCategory(category) {
        return this.getAll().filter(widget => widget.category === category);
    }

    /**
     * Get all widgets available to a user based on their permissions
     */
    getAvailableWidgets(userPermissions = []) {
        return this.getAll().filter((widget) => this.isWidgetAvailable(widget.id, userPermissions));
    }

    /**
     * Get available widgets grouped by category
     */
    getAvailableWidgetsByCategory(userPermissions = []) {
        const available = this.getAvailableWidgets(userPermissions);
        return {
            internal: available.filter(w => w.category === 'internal'),
            management: available.filter(w => w.category === 'management'),
            client: available.filter(w => w.category === 'client'),
        };
    }

    /**
     * Check if a widget is available to a user
     */
    isWidgetAvailable(widgetId, userPermissions = []) {
        const widget = this.getById(widgetId);
        if (!widget) return false;

        const positives = widget.permissions || [];
        const negatives = widget.negativePermissions || [];

        const positiveOk = positives.length === 0
            || positives.some((p) => userPermissions.includes(p));
        const negativeOk = !negatives.some((n) => userPermissions.includes(n));

        return positiveOk && negativeOk;
    }

    /**
     * Get the component for a widget type
     */
    getComponent(widgetId) {
        const widget = this.getById(widgetId);
        return widget ? widget.component : null;
    }
}

// Export singleton instance
export default new WidgetRegistry();
